import { useEffect, useRef, useState } from 'react'
import { Modal } from './Modal'
import { formatAccessKey } from '../lib/accessKey'
import { copyToClipboard } from '../lib/clipboard'
import { TIMEOUTS } from '../config/constants'

interface ShowKeyOnceProps {
  open: boolean
  /** Normalized 12-char plaintext key (no dashes). */
  plaintext: string
  title: string
  /** Contextual message above the key box (e.g. "Key created" vs "Key regenerated"). */
  subtitle: string
  onClose: () => void
}

/**
 * One-shot "copy this key now" modal, shared between create and regenerate.
 * After close, the key is unrecoverable — the backend never exposes it again.
 * Keeping both flows in one component ensures the warning text and copy
 * affordance stay identical.
 */
export function ShowKeyOnce({ open, plaintext, title, subtitle, onClose }: ShowKeyOnceProps) {
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)
  // copyToClipboard's await can outlive the modal — operator closes via Esc
  // or "Закрыть" while the clipboard write is in flight. Without these
  // refs the post-await setTimeout fires on the dead component, calls
  // setCopied(false), and leaks the closure + setter (plus a React
  // warning). Canonical fix mirrored from AccessKeyCell.tsx
  // (commit 3346462 2026-05-21).
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mounted = useRef(true)

  useEffect(() => () => {
    mounted.current = false
    if (copyResetTimer.current !== null) clearTimeout(copyResetTimer.current)
  }, [])

  const copy = async () => {
    setCopyFailed(false)
    const ok = await copyToClipboard(plaintext)
    if (!mounted.current) return
    if (ok) {
      setCopied(true)
      if (copyResetTimer.current !== null) clearTimeout(copyResetTimer.current)
      copyResetTimer.current = setTimeout(
        () => setCopied(false),
        TIMEOUTS.CLIPBOARD_FEEDBACK_MS,
      )
    } else {
      setCopyFailed(true)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed break-words">
        {subtitle}
      </p>
      <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-4 mb-4">
        <p
          data-testid="plaintext-access-key"
          className="font-mono text-lg tracking-wider text-center text-gray-900 dark:text-gray-100 select-all"
        >
          {formatAccessKey(plaintext)}
        </p>
      </div>
      <p className="text-xs text-amber-700 dark:text-amber-400 mb-4 leading-relaxed break-words">
        ⚠️ Ключ показывается один раз. Скопируйте и сохраните сейчас — после закрытия восстановить невозможно.
      </p>
      {copyFailed && (
        <p className="text-xs text-red-600 dark:text-red-400 mb-3">
          Не удалось скопировать. Выделите ключ вручную (Ctrl+A, Ctrl+C).
        </p>
      )}
      <div className="flex justify-end gap-3">
        <button
          onClick={copy}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
        >
          {copied ? 'Скопировано' : 'Скопировать'}
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        >
          Закрыть
        </button>
      </div>
    </Modal>
  )
}
